import { useState, useEffect } from "react";
import { requestLocationOnUserAction, getCachedLocation } from "@/utils/location";
import { toastWarning } from "@/utils/toast";

interface TripInfo {
  pickupTime: string;
  pickupLocation: string;
  returnTime: string;
  returnLocation: string;
  days: number;
}

interface EditTripModalProps {
  initialData?: TripInfo | null;
  onClose: () => void;
  onConfirm: (info: TripInfo) => void;
}

export function EditTripModal({ initialData, onClose, onConfirm }: EditTripModalProps) {
  // 从缓存获取位置名称作为默认值
  const cached = getCachedLocation();
  const defaultLocationName = cached?.city || cached?.district || "";

  // 计算租期天数
  const calculateDays = (pickup: string, returnD: string) => {
    if (!pickup || !returnD) return 1;
    const pickupDate = new Date(pickup);
    const returnDate = new Date(returnD);
    const diffTime = returnDate.getTime() - pickupDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 1;
  };

  const today = new Date();
  const defaultPickup = today.toISOString().split('T')[0];
  const defaultReturn = new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [pickupDate, setPickupDate] = useState(initialData?.pickupTime?.split(' ')[0] || defaultPickup);
  const [returnDate, setReturnDate] = useState(initialData?.returnTime?.split(' ')[0] || defaultReturn);
  const [pickupLocation, setPickupLocation] = useState(initialData?.pickupLocation || defaultLocationName);
  const [returnLocation, setReturnLocation] = useState(initialData?.returnLocation || defaultLocationName);
  const [sameReturn, setSameReturn] = useState(!initialData?.returnLocation || initialData.returnLocation === initialData?.pickupLocation);
  const [locating, setLocating] = useState(false);
  const [locateResult, setLocateResult] = useState<string | null>(null);

  // 弹窗打开时锁定背景滚动
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleLocate = async () => {
    setLocating(true);
    setLocateResult(null);
    const result = await requestLocationOnUserAction();
    if (result.success && result.location) {
      const address = result.location.formattedAddress || result.location.address || "当前位置";
      setLocateResult(address);
      setPickupLocation(`${address}（当前位置）`);
    } else {
      toastWarning(`定位失败: ${result.error || "请检查定位权限"}`);
    }
    setLocating(false);
  };

  const handleConfirm = () => {
    if (!pickupLocation) {
      toastWarning("请输入取车地点");
      return;
    }
    const info: TripInfo = {
      pickupTime: pickupDate,
      pickupLocation,
      returnTime: returnDate,
      returnLocation: sameReturn ? pickupLocation : returnLocation,
      days: calculateDays(pickupDate, returnDate),
    };
    onConfirm(info);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}>
      <div className="bg-white rounded-t-3xl w-full" style={{ maxWidth: 480, maxHeight: "90vh", overflow: "hidden" }}>
        <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mt-3 flex-shrink-0" />
        <div className="flex items-center justify-between px-5 pt-4 pb-3" style={{ borderBottom: "1px solid #F5F5F5" }}>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: "#F4F5F9" }}>
            <i className="ri-close-line" style={{ color: "#374151", fontSize: 18 }} />
          </button>
          <span className="text-base font-bold text-gray-900">填写取还车信息</span>
          <div className="w-8" />
        </div>

        <div className="px-5 py-4 space-y-5 pb-8">
          {/* 取车信息 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full" style={{ background: "#1E3A8A" }} />
              <span className="text-sm font-bold text-gray-900">取车信息</span>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-gray-500 mb-1.5">取车日期</div>
                <input type="date" value={pickupDate} min={new Date().toISOString().split('T')[0]} onChange={e => {
                  const newDate = e.target.value;
                  setPickupDate(newDate);
                  if (returnDate < newDate) {
                    setReturnDate(newDate);
                  }
                }}
                  className="w-full px-3 py-2.5 rounded-xl text-sm outline-none cursor-pointer"
                  style={{ background: "#F8F9FC", border: "1.5px solid #E5E7EB" }} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-gray-500">取车地点</span>
                  <button
                    onClick={handleLocate}
                    className="flex items-center gap-1 text-xs font-medium cursor-pointer whitespace-nowrap"
                    style={{ color: "#1E3A8A" }}
                  >
                    {locating
                      ? <><i className="ri-loader-4-line animate-spin" style={{ fontSize: 12 }} />定位中...</>
                      : <><i className="ri-map-pin-2-line" style={{ fontSize: 12 }} />使用当前位置</>
                    }
                  </button>
                </div>
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: "#F8F9FC", border: "1.5px solid #E5E7EB" }}>
                  <i className="ri-map-pin-line" style={{ color: "#9CA3AF", fontSize: 15 }} />
                  <input
                    value={pickupLocation}
                    onChange={e => setPickupLocation(e.target.value)}
                    className="flex-1 bg-transparent outline-none text-sm text-gray-700"
                    placeholder="输入取车地点"
                  />
                </div>
                {locateResult && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <i className="ri-checkbox-circle-fill" style={{ color: "#10B981", fontSize: 12 }} />
                    <span className="text-xs" style={{ color: "#10B981" }}>已定位：{locateResult}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 还车信息 */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full border-2" style={{ borderColor: "#1E3A8A" }} />
                <span className="text-sm font-bold text-gray-900">还车信息</span>
              </div>
              <button onClick={() => setSameReturn(!sameReturn)} className="flex items-center gap-1.5 cursor-pointer">
                <div className="w-9 h-5 rounded-full flex items-center px-0.5 transition-all" style={{ background: sameReturn ? "#1E3A8A" : "#D1D5DB" }}>
                  <div className="w-4 h-4 rounded-full bg-white transition-all" style={{ transform: sameReturn ? "translateX(16px)" : "translateX(0)" }} />
                </div>
                <span className="text-xs text-gray-500">同取车地点</span>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-gray-500 mb-1.5">还车日期</div>
                <input type="date" value={returnDate} min={new Date().toISOString().split('T')[0]} onChange={e => {
                  const newDate = e.target.value;
                  setReturnDate(newDate);
                  if (newDate < pickupDate) {
                    setPickupDate(newDate);
                  }
                }}
                  className="w-full px-3 py-2.5 rounded-xl text-sm outline-none cursor-pointer"
                  style={{ background: "#F8F9FC", border: "1.5px solid #E5E7EB" }} />
              </div>
              {!sameReturn && (
                <div>
                  <div className="text-xs text-gray-500 mb-1.5">还车地点</div>
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: "#F8F9FC", border: "1.5px solid #E5E7EB" }}>
                    <i className="ri-map-pin-line" style={{ color: "#9CA3AF", fontSize: 15 }} />
                    <input
                      value={returnLocation}
                      onChange={e => setReturnLocation(e.target.value)}
                      className="flex-1 bg-transparent outline-none text-sm text-gray-700"
                      placeholder="输入还车地点"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl" style={{ background: "#EFF6FF" }}>
            <i className="ri-information-line flex-shrink-0 mt-0.5" style={{ color: "#2563EB", fontSize: 14 }} />
            <span className="text-xs text-blue-700 leading-relaxed">请确认车辆是否可在指定地点服务，客服将在提交后与您确认</span>
          </div>

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap" style={{ background: "#F3F4F6", color: "#6B7280" }}>取消</button>
            <button onClick={handleConfirm} className="flex-1 py-3 rounded-full text-sm font-semibold text-white cursor-pointer whitespace-nowrap" style={{ background: "#1E3A8A" }}>确认添加</button>
          </div>
        </div>
      </div>
    </div>
  );
}
